import { useEffect, useState } from 'react';
import {
  Box,
  Button,
  MenuItem,
  Select,
  Stack,
  Typography,
} from '@mui/material';
import MuiCard from '@mui/material/Card';
import { styled } from '@mui/material/styles';
import { useTranslation } from 'react-i18next';
import { appLocales, languageNames } from 'features/i18n/locales';
import { getAppLocale } from 'features/i18n/utils/getAppLocale';
import { signinRetryPath } from '../utils/authTarget';
import type { AuthErrorType } from '../types/authError';
import type { SelectChangeEvent } from '@mui/material/Select';

const Container = styled(Stack)(({ theme }) => ({
  height: '100dvh',
  minHeight: '100%',
  padding: theme.spacing(2),
}));

const Card = styled(MuiCard)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  alignSelf: 'center',
  width: '100%',
  padding: theme.spacing(4),
  gap: theme.spacing(2),
  margin: 'auto',
  [theme.breakpoints.up('sm')]: {
    maxWidth: '450px',
  },
}));

type AuthErrorPageProps = {
  errorType: AuthErrorType;
};

const STORAGE_KEY = 'authErrorPageLanguage';

const getErrorCategory = (
  errorType: AuthErrorType
): 'connection' | 'authService' | 'unknown' => {
  switch (errorType) {
    case 'network':
    case 'timeout':
      return 'connection';
    case 'service-down':
    case 'token-exchange':
    case 'firebase-auth':
    case 'oidc-callback':
    case 'oidc-config':
      return 'authService';
    case 'unknown':
      return 'unknown';
  }
};

export const AuthErrorPage = ({ errorType }: AuthErrorPageProps) => {
  const { t, i18n } = useTranslation();
  const [language, setLanguage] = useState(i18n.language);

  // Browser locale detection on mount
  useEffect(() => {
    const savedLanguage = localStorage.getItem(STORAGE_KEY);
    if (savedLanguage) {
      i18n.changeLanguage(savedLanguage).catch(console.error);
      setLanguage(savedLanguage);
      return;
    }

    const browserLocale = navigator.language;
    const normalizedLocale = getAppLocale(browserLocale);
    i18n.changeLanguage(normalizedLocale).catch(console.error);
    setLanguage(normalizedLocale);
  }, [i18n]);

  const handleLanguageChange = (event: SelectChangeEvent) => {
    const newLanguage = event.target.value;
    setLanguage(newLanguage);
    void i18n.changeLanguage(newLanguage).catch(console.error);
    localStorage.setItem(STORAGE_KEY, newLanguage);
  };

  const errorCategory = getErrorCategory(errorType);

  return (
    <Container direction="column" justifyContent="space-between">
      <Card elevation={3}>
        <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 1 }}>
          <Select
            value={language}
            onChange={handleLanguageChange}
            size="small"
            sx={{ minWidth: 150 }}
            inputProps={{ 'aria-label': t('Language') }}
          >
            {appLocales.map(locale => (
              <MenuItem key={locale} value={locale}>
                {languageNames[locale]}
              </MenuItem>
            ))}
          </Select>
        </Box>
        <Typography component="h1" variant="h4">
          {t(`authError.${errorCategory}.title`)}
        </Typography>
        <Typography sx={{ width: '100%', mb: 2 }}>
          {t(`authError.${errorCategory}.message`)}
        </Typography>
        <Button
          fullWidth
          variant="contained"
          onClick={() => {
            window.location.href = signinRetryPath();
          }}
        >
          {t('authError.tryAgain')}
        </Button>
      </Card>
    </Container>
  );
};
